<template>
  <div class="category-management">
    <div class="page-header">
      <h2>分类管理</h2>
      <el-button type="primary" @click="showCreateDialog">新增分类</el-button>
    </div>

    <!-- 搜索表单 -->
    <el-form :inline="true" :model="searchForm" class="search-form">
      <el-form-item label="分类名称">
        <el-input v-model="searchForm.category_name" placeholder="请输入分类名称" clearable />
      </el-form-item>
      <el-form-item label="层级">
        <el-select v-model="searchForm.level" placeholder="请选择层级" clearable>
          <el-option label="一级分类" :value="1" />
          <el-option label="二级分类" :value="2" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleSearch">搜索</el-button>
        <el-button @click="handleReset">重置</el-button>
      </el-form-item>
    </el-form>

    <!-- 数据表格 -->
    <el-table v-loading="loading" :data="categoryList" border style="width: 100%">
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="category_name" label="分类名称" />
      <el-table-column prop="category_icon" label="图标" width="100">
        <template slot-scope="scope">
          <el-image v-if="scope.row.category_icon" :src="scope.row.category_icon" style="width: 40px; height: 40px" fit="cover" />
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column prop="parent_id" label="父级分类" width="100">
        <template slot-scope="scope">
          {{ scope.row.parent_id == 0 ? '顶级分类' : scope.row.parent_name }}
        </template>
      </el-table-column>
      <el-table-column prop="level" label="层级" width="80" />
      <el-table-column prop="sort_order" label="排序" width="80" />
      <el-table-column prop="is_show" label="状态" width="100">
        <template slot-scope="scope">
          <el-tag :type="scope.row.is_show == 1 ? 'success' : 'danger'">
            {{ scope.row.is_show == 1 ? '显示' : '隐藏' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="create_time" label="创建时间" width="180" />
      <el-table-column label="操作" width="220" fixed="right">
        <template slot-scope="scope">
          <div class="table-operations">
            <el-button size="mini" type="text" @click="handleView(scope.row)">查看</el-button>
            <el-button size="mini" type="text" class="edit-btn" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button size="mini" type="text" class="delete-btn" @click="handleDelete(scope.row)">删除</el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination">
      <el-pagination
        :current-page="pagination.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.limit"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- 创建/编辑对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="600px">
      <el-form ref="categoryForm" :model="categoryForm" :rules="categoryRules" label-width="120px">
        <el-form-item label="分类名称" prop="category_name">
          <el-input v-model="categoryForm.category_name" placeholder="请输入分类名称" />
        </el-form-item>
        <el-form-item label="父级分类" prop="parent_id">
          <el-select v-model="categoryForm.parent_id" placeholder="请选择父级分类" style="width: 100%" @change="handleParentChange">
            <el-option label="顶级分类" :value="0" />
            <el-option
              v-for="category in availableParentCategories"
              :key="category.id"
              :label="category.category_name"
              :value="category.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="层级" prop="level">
          <el-input v-model="categoryForm.level" disabled style="width: 100%" />
          <div style="color: #909399; font-size: 12px; margin-top: 5px;">
            层级根据父级分类自动计算（最多支持二级分类）
          </div>
        </el-form-item>
        <el-form-item label="分类图标" prop="category_icon">
          <div class="image-upload-container">
            <el-upload
              class="image-uploader"
              :action="uploadAction"
              :show-file-list="false"
              :before-upload="(file) => beforeImageUpload(file, 'icon')"
              :headers="uploadHeaders"
            >
              <img v-if="categoryForm.category_icon" :src="categoryForm.category_icon" class="image-preview">
              <i v-else class="el-icon-plus image-uploader-icon"></i>
            </el-upload>
            <div class="image-upload-tips">
              <p>支持 JPG、PNG、GIF、WEBP 格式</p>
              <p>图片大小不超过 5MB</p>
              <p>建议尺寸：100x100 像素</p>
            </div>
          </div>
        </el-form-item>
        <el-form-item label="分类图片" prop="category_image">
          <div class="image-upload-container">
            <el-upload
              class="image-uploader banner-uploader"
              :action="uploadAction"
              :show-file-list="false"
              :before-upload="(file) => beforeImageUpload(file, 'image')"
              :headers="uploadHeaders"
            >
              <img v-if="categoryForm.category_image" :src="categoryForm.category_image" class="image-preview banner-preview">
              <i v-else class="el-icon-plus image-uploader-icon"></i>
            </el-upload>
            <div class="image-upload-tips">
              <p>支持 JPG、PNG、GIF、WEBP 格式</p>
              <p>图片大小不超过 5MB</p>
              <p>建议尺寸：750x400 像素</p>
            </div>
          </div>
        </el-form-item>
        <el-form-item label="排序" prop="sort_order">
          <el-input-number v-model="categoryForm.sort_order" :min="0" :max="9999" />
        </el-form-item>
        <el-form-item label="是否显示" prop="is_show">
          <el-radio-group v-model="categoryForm.is_show">
            <el-radio :label="1">显示</el-radio>
            <el-radio :label="0">隐藏</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="SEO标题" prop="seo_title">
          <el-input v-model="categoryForm.seo_title" placeholder="请输入SEO标题" />
        </el-form-item>
        <el-form-item label="SEO关键词" prop="seo_keywords">
          <el-input v-model="categoryForm.seo_keywords" placeholder="请输入SEO关键词，多个用逗号分隔" />
        </el-form-item>
        <el-form-item label="SEO描述" prop="seo_desc">
          <el-input v-model="categoryForm.seo_desc" type="textarea" :rows="3" placeholder="请输入SEO描述" />
        </el-form-item>
        <el-form-item label="分类描述" prop="category_desc">
          <el-input v-model="categoryForm.category_desc" type="textarea" :rows="3" placeholder="请输入分类描述" />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="handleSubmit">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 查看详情对话框 -->
    <el-dialog title="分类详情" :visible.sync="detailVisible" width="900px">
      <div v-if="currentCategory" class="category-detail">
        <!-- 基本信息 -->
        <div class="detail-section">
          <div class="section-title">基本信息</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">分类ID</div>
              <div class="detail-value">{{ currentCategory.id }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">分类名称</div>
              <div class="detail-value">{{ currentCategory.category_name }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">父级分类</div>
              <div class="detail-value">
                {{ currentCategory.parent_id == 0 ? '顶级分类' : currentCategory.parent_name || '-' }}
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">层级</div>
              <div class="detail-value">{{ currentCategory.level }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">排序</div>
              <div class="detail-value">{{ currentCategory.sort_order }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">状态</div>
              <div class="detail-value">
                <el-tag :type="currentCategory.is_show == 1 ? 'success' : 'danger'">
                  {{ currentCategory.is_show == 1 ? '显示' : '隐藏' }}
                </el-tag>
              </div>
            </div>
          </div>
        </div>

        <!-- 图片信息 -->
        <div class="detail-section">
          <div class="section-title">图片信息</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">分类图标</div>
              <div class="detail-value">
                <el-image 
                  v-if="currentCategory.category_icon" 
                  :src="currentCategory.category_icon" 
                  style="width: 60px; height: 60px; border-radius: 4px; cursor: pointer;"
                  fit="cover"
                  :preview-src-list="[currentCategory.category_icon]"
                />
                <span v-else>-</span>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">分类图片</div>
              <div class="detail-value">
                <el-image 
                  v-if="currentCategory.category_image" 
                  :src="currentCategory.category_image" 
                  style="width: 200px; height: 120px; border-radius: 4px; cursor: pointer;"
                  fit="cover"
                  :preview-src-list="[currentCategory.category_image]"
                />
                <span v-else>-</span>
              </div>
            </div>
          </div>
        </div>

        <!-- SEO信息 -->
        <div class="detail-section">
          <div class="section-title">SEO信息</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">SEO标题</div>
              <div class="detail-value">{{ currentCategory.seo_title || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">SEO关键词</div>
              <div class="detail-value">{{ currentCategory.seo_keywords || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">SEO描述</div>
              <div class="detail-value">{{ currentCategory.seo_desc || '-' }}</div>
            </div>
          </div>
        </div>

        <!-- 其他信息 -->
        <div class="detail-section">
          <div class="section-title">其他信息</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">分类描述</div>
              <div class="detail-value">{{ currentCategory.category_desc || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">创建时间</div>
              <div class="detail-value">{{ currentCategory.create_time || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">更新时间</div>
              <div class="detail-value">{{ currentCategory.update_time || '-' }}</div>
            </div>
          </div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="detailVisible = false">关闭</el-button>
        <el-button type="primary" @click="handleEditFromDetail">编辑</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getCategoryList, getCategoryDetail, createCategory, updateCategory, deleteCategory } from '@/api/store'
import { uploadImage } from '@/api/upload'

export default {
  name: 'CategoryManagement',
  data() {
    return {
      loading: false,
      categoryList: [],
      parentCategories: [],
      searchForm: {
        category_name: '',
        level: ''
      },
      pagination: {
        page: 1,
        limit: 20,
        total: 0
      },
      dialogVisible: false,
      detailVisible: false,
      dialogTitle: '新增分类',
      currentCategory: null,
      categoryForm: {
        id: null,
        parent_id: 0,
        category_name: '',
        category_icon: '',
        category_image: '',
        category_desc: '',
        level: 1,
        sort_order: 100,
        is_show: 1,
        seo_title: '',
        seo_keywords: '',
        seo_desc: ''
      },
      categoryRules: {
        category_name: [
          { required: true, message: '请输入分类名称', trigger: 'blur' },
          { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
        ],
        parent_id: [
          { required: true, message: '请选择父级分类', trigger: 'change' }
        ]
      },
      // 上传相关
      uploadAction: '',
      uploadHeaders: {},
      uploadLoading: null
    }
  },
  mounted() {
    this.fetchCategoryList()
    this.fetchParentCategories()
    this.initUpload()
  },
  computed: {
    // 可用的父级分类（只显示一级分类）
    availableParentCategories() {
      return this.parentCategories.filter(cat => cat.level === 1)
    }
  },
  methods: {
    // 初始化上传配置
    initUpload() {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      this.uploadHeaders = {
        'Authorization': `Bearer ${token}`
      }
      this.uploadAction = ''
    },
    
    // 上传前验证
    beforeImageUpload(file, type) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
      const isImage = allowedTypes.includes(file.type)
      if (!isImage) {
        this.$message.error('只能上传 JPG、PNG、GIF、WEBP 格式的图片！')
        return false
      }
      
      const maxSize = 5 * 1024 * 1024 // 5MB
      const isLt5M = file.size <= maxSize
      if (!isLt5M) {
        this.$message.error('图片大小不能超过 5MB！')
        return false
      }
      
      this.uploadLoading = this.$loading({
        lock: true,
        text: '上传中...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })
      
      this.handleCustomUpload(file, type)
      return false
    },
    
    // 自定义上传方法
    async handleCustomUpload(file, type) {
      try {
        const path = type === 'icon' ? 'admin/category/icons' : 'admin/category/images'
        const response = await uploadImage(file, path)
        
        if (response.code === 200) {
          if (type === 'icon') {
            this.categoryForm.category_icon = response.data.url
          } else {
            this.categoryForm.category_image = response.data.url
          }
          this.$message.success('图片上传成功！')
        } else {
          this.$message.error(response.msg || '上传失败')
        }
      } catch (error) {
        console.error('上传失败:', error)
        this.$message.error('上传失败，请重试')
      } finally {
        if (this.uploadLoading) {
          this.uploadLoading.close()
        }
      }
    },
    
    async fetchCategoryList() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.page,
          limit: this.pagination.limit,
          ...this.searchForm
        }
        const response = await getCategoryList(params)
        this.categoryList = response.data.list || []
        this.pagination.total = response.data.total || 0
      } catch (error) {
        console.error('获取分类列表失败:', error)
      } finally {
        this.loading = false
      }
    },
    async fetchParentCategories() {
      try {
        const response = await getCategoryList({ page: 1, limit: 1000 })
        this.parentCategories = response.data.list || []
      } catch (error) {
        console.error('获取父级分类失败:', error)
      }
    },
    handleSearch() {
      this.pagination.page = 1
      this.fetchCategoryList()
    },
    handleReset() {
      this.searchForm = {
        category_name: '',
        level: ''
      }
      this.pagination.page = 1
      this.fetchCategoryList()
    },
    handleSizeChange(size) {
      this.pagination.limit = size
      this.fetchCategoryList()
    },
    handleCurrentChange(page) {
      this.pagination.page = page
      this.fetchCategoryList()
    },
    showCreateDialog() {
      this.dialogTitle = '新增分类'
      this.categoryForm = {
        id: null,
        parent_id: 0,
        category_name: '',
        category_icon: '',
        category_image: '',
        category_desc: '',
        level: 1,
        sort_order: 100,
        is_show: 1,
        seo_title: '',
        seo_keywords: '',
        seo_desc: ''
      }
      this.dialogVisible = true
    },
    // 处理父级分类变化
    handleParentChange(parentId) {
      if (parentId === 0) {
        this.categoryForm.level = 1
      } else {
        // 查找父级分类的层级
        const parentCategory = this.parentCategories.find(cat => cat.id === parentId)
        if (parentCategory) {
          if (parentCategory.level >= 2) {
            this.$message.warning('最多只支持二级分类，请选择一级分类作为父级')
            this.categoryForm.parent_id = 0
            this.categoryForm.level = 1
          } else {
            this.categoryForm.level = parentCategory.level + 1
          }
        }
      }
    },
    async handleView(row) {
      try {
        const response = await getCategoryDetail(row.id)
        this.currentCategory = response.data
        this.detailVisible = true
      } catch (error) {
        console.error('获取分类详情失败:', error)
        this.$message.error('获取分类详情失败')
      }
    },
    async handleEdit(row) {
      try {
        const response = await getCategoryDetail(row.id)
        this.categoryForm = response.data
        this.dialogTitle = '编辑分类'
        this.dialogVisible = true
      } catch (error) {
        console.error('获取分类详情失败:', error)
      }
    },
    handleDelete(row) {
      this.$confirm('确认删除该分类吗？删除后不可恢复！', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await deleteCategory(row.id)
          this.$message.success('删除成功')
          this.fetchCategoryList()
          this.fetchParentCategories()
        } catch (error) {
          console.error('删除分类失败:', error)
        }
      })
    },
    handleSubmit() {
      this.$refs.categoryForm.validate(async (valid) => {
        if (valid) {
          try {
            if (this.categoryForm.id) {
              await updateCategory(this.categoryForm)
              this.$message.success('更新成功')
            } else {
              await createCategory(this.categoryForm)
              this.$message.success('创建成功')
            }
            this.dialogVisible = false
            this.fetchCategoryList()
            this.fetchParentCategories()
          } catch (error) {
            console.error('操作失败:', error)
          }
        }
      })
    },
    handleEditFromDetail() {
      this.detailVisible = false
      this.handleEdit(this.currentCategory)
    }
  }
}
</script>

<style scoped>
.category-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-size: 24px;
  color: #333;
}

.search-form {
  background: #f5f5f5;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  text-align: right;
}

.table-operations {
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: nowrap;
}

.table-operations .el-button--text {
  padding: 2px 8px;
  font-size: 12px;
  border: none;
  background: none;
}

.table-operations .edit-btn {
  color: #409eff;
}

.table-operations .edit-btn:hover {
  color: #66b1ff;
  background-color: #ecf5ff;
}

.table-operations .delete-btn {
  color: #f56c6c;
}

.table-operations .delete-btn:hover {
  color: #f78989;
  background-color: #fef0f0;
}

.category-detail {
  padding: 10px 0;
}

.detail-section {
  margin-bottom: 30px;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 15px;
  padding-left: 10px;
  border-left: 4px solid #409EFF;
  line-height: 20px;
}

.detail-table {
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  overflow: hidden;
  background-color: #fff;
}

.detail-row {
  display: flex;
  border-bottom: 1px solid #EBEEF5;
  min-height: 50px;
  align-items: center;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  font-weight: 500;
  color: #606266;
  width: 150px;
  text-align: right;
  padding: 15px 20px;
  background-color: #FAFAFA;
  border-right: 1px solid #EBEEF5;
  flex-shrink: 0;
  box-sizing: border-box;
}

.detail-value {
  color: #303133;
  word-break: break-word;
  padding: 15px 20px;
  flex: 1;
  box-sizing: border-box;
}

/* 图片上传样式 */
.image-upload-container {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.image-uploader {
  flex-shrink: 0;
}

.image-uploader >>> .el-upload {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.3s;
  width: 148px;
  height: 148px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-uploader >>> .el-upload:hover {
  border-color: #409EFF;
}

.banner-uploader >>> .el-upload {
  width: 300px;
  height: 160px;
}

.image-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 148px;
  height: 148px;
  line-height: 148px;
  text-align: center;
}

.image-preview {
  width: 148px;
  height: 148px;
  display: block;
  object-fit: cover;
}

.banner-preview {
  width: 300px;
  height: 160px;
}

.image-upload-tips {
  flex: 1;
  color: #909399;
  font-size: 12px;
  line-height: 1.8;
}

.image-upload-tips p {
  margin: 0;
  padding: 2px 0;
}
</style>